import React, { useEffect, useRef, useState } from 'react';
import { mediaGateways, preferredGateway, useMediaConfig, ipfsAvailability } from 'services/media';
import ShellIcon from './ShellIcon';

let nextId = 0;
export default function VideoSourceMenu({ value, onChange, cid }: { value: string; onChange: (source: string) => void; cid?: string }) {
  const config = useMediaConfig();
  const [availability, setAvailability] = useState<{ cid: string; ready: boolean } | null>(null);
  const ready = availability?.cid === cid ? availability.ready : null;
  const sources = mediaGateways().filter(source => source === config.r2PublicBaseUrl || ready === true);
  useEffect(() => {
    const abort = new AbortController();
    if (!cid || !config.ipfsEnabled) { setAvailability(null); return () => abort.abort(); }
    ipfsAvailability(cid, abort.signal).then(available => {
      if (!abort.signal.aborted) setAvailability({ cid, ready: available });
    });
    return () => abort.abort();
  }, [cid, config.ipfsEnabled]);
  useEffect(() => {
    if (ready === false && preferredGateway(value) !== config.r2PublicBaseUrl) onChange(config.r2PublicBaseUrl);
  }, [ready, value, config.r2PublicBaseUrl, onChange]);
  const selected = preferredGateway(value);
  const isSpeem = selected === config.r2PublicBaseUrl;
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(258);
  const [menuHeight, setMenuHeight] = useState(300);
  const [id] = useState(() => `video-source-${++nextId}`);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const fit = () => {
      const player = container.current?.closest('.video-layout,.pro-video-wrapper');
      const bottom = Math.min(player?.getBoundingClientRect().bottom || window.innerHeight, window.innerHeight);
      setMenuHeight(Math.max(80, bottom - (trigger.current?.getBoundingClientRect().bottom || 0) - 16));
      setMenuWidth(Math.max(120, Math.min(258, (player?.getBoundingClientRect().width || window.innerWidth - 12) - 28)));
    };
    fit();
    container.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    const dismiss = (event: PointerEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', fit);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape); window.removeEventListener('resize', fit); };
  }, [open]);
  return <div className="video-source-menu" ref={container} onWheel={event => event.stopPropagation()} onTouchStart={event => event.stopPropagation()} onTouchMove={event => event.stopPropagation()} onTouchEnd={event => event.stopPropagation()} onClick={event => event.stopPropagation()} onKeyDown={event => {
    event.stopPropagation();
    if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
  }}>
    <button type="button" className="video-source-trigger" ref={trigger} aria-label={`Playback source: ${isSpeem ? 'Speem' : 'IPFS'}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}>
      {isSpeem ? <img src="/assets/img/logo-green.png" className="video-source-logo" alt="" /> : <img src="/assets/img/ipfs-icon.svg" className="video-source-logo" alt="" />}
      <ShellIcon name="chevron" size={13} />
    </button>
    {open && <div id={id} className="video-source-popover" style={{ width: menuWidth, maxHeight: menuHeight }} role="dialog" aria-label="Playback source">
      <fieldset><legend>Playback source</legend>
        {sources.map(source => {
          const speem = source === config.r2PublicBaseUrl;
          return <label className={`video-source-option ${selected === source ? 'is-selected' : ''}`} key={source}>
            {speem ? <img src="/assets/img/logo-green.png" className="video-source-logo" alt="" /> : <img src="/assets/img/ipfs-icon.svg" className="video-source-logo" alt="" />}
            <span className="video-source-copy"><strong>{speem ? 'Speem' : 'IPFS'}</strong></span>
            <input type="radio" name={id} value={source} checked={selected === source} onChange={() => {
              onChange(source); setOpen(false); trigger.current?.focus();
            }} />
          </label>;
        })}
        {(!config.ipfsEnabled || ready !== true) && <div className="video-source-unavailable" aria-disabled="true"><img src="/assets/img/ipfs-icon.svg" className="video-source-logo" alt="" /><span><strong>IPFS</strong></span></div>}
      </fieldset>
    </div>}
  </div>;
}
